import React, { useEffect, useRef, useState } from "react";
import {
  RadioTower, Plug, CheckCircle2, AlertTriangle, Loader2, RefreshCw, Trash2,
  Music2, Users, Activity, Gauge, ListMusic, Play, Square, SkipForward,
  RotateCcw, ShieldCheck, Network, X as XIcon, ChevronRight, Disc3,
} from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { appConfirm } from "@/components/AppDialogHost";

const timeAgo = (iso) => {
  if (!iso) return "never";
  const s = Math.max(0, (Date.now() - new Date(iso).getTime()) / 1000);
  if (s < 60) return "just now";
  if (s < 3600) return `${Math.round(s / 60)}m ago`;
  if (s < 86400) return `${Math.round(s / 3600)}h ago`;
  return `${Math.round(s / 86400)}d ago`;
};

const CAP_LABELS = {
  now_playing: "Now Playing", listeners: "Listeners", history: "Song History",
  playlists: "Playlists", autodj_control: "AutoDJ Control", server_control: "Server Control",
  skip_track: "Skip Track", stream_status: "Stream Status",
};

export default function BroadcastHub() {
  const [tab, setTab] = useState("dashboard");
  const [connections, setConnections] = useState([]);
  const [loading, setLoading] = useState(true);

  const loadConns = () =>
    api.get("/broadcast/connections").then((r) => setConnections(r.data || [])).catch(() => {});

  useEffect(() => { loadConns().finally?.(() => {}); setLoading(false); }, []);

  return (
    <div className="p-6 md:p-10 animate-fade-up max-w-7xl mx-auto" data-testid="broadcast-hub-page">
      <div className="flex items-center gap-3">
        <RadioTower className="h-7 w-7 text-brand-amber" />
        <div>
          <h1 className="text-2xl md:text-3xl font-black text-ink-primary tracking-tight">Broadcast Hub</h1>
          <p className="text-xs text-ink-muted mt-0.5">
            Connect your existing broadcasting system — Centova Cast, SHOUTcast, Icecast, AzuraCast, MediaCP, SonicPanel, Everest Cast and more — without changing hosting.
          </p>
        </div>
      </div>

      <div className="mt-6 flex gap-1 border-b border-line">
        {[
          { id: "dashboard", label: "Radio Dashboard" },
          { id: "network", label: "Network Control Room" },
          { id: "connections", label: "Connections" },
        ].map((t) => (
          <button
            key={t.id}
            onClick={() => setTab(t.id)}
            data-testid={`bh-tab-${t.id}`}
            className={`px-4 py-2 text-xs font-mono uppercase tracking-wider border-b-2 -mb-px transition ${
              tab === t.id ? "border-brand-amber text-brand-amber" : "border-transparent text-ink-muted hover:text-ink-primary"}`}
          >{t.label}</button>
        ))}
      </div>

      {tab === "dashboard" && <DashboardTab connections={connections} onNeedConnection={() => setTab("connections")} />}
      {tab === "network" && <NetworkTab />}
      {tab === "connections" && <ConnectionsTab connections={connections} reload={loadConns} />}
    </div>
  );
}

/* ─────────────────────────── Radio Dashboard ─────────────────────────── */

function DashboardTab({ connections, onNeedConnection }) {
  const [connId, setConnId] = useState("");
  const [data, setData] = useState(null);
  const [trends, setTrends] = useState([]);
  const [busy, setBusy] = useState(false);
  const timer = useRef(null);

  const active = connections.find((c) => c.conn_id === connId) || connections[0];

  const load = (id) => {
    if (!id) return;
    setBusy(true);
    api.get(`/broadcast/connections/${id}/dashboard`)
      .then((r) => setData(r.data))
      .catch(() => {})
      .finally(() => setBusy(false));
    api.get(`/broadcast/connections/${id}/trends?hours=24`)
      .then((r) => setTrends(r.data.samples || [])).catch(() => {});
  };

  useEffect(() => {
    if (!active) return;
    setConnId(active.conn_id);
    load(active.conn_id);
    timer.current = setInterval(() => load(active.conn_id), 25000);
    return () => clearInterval(timer.current);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [active?.conn_id]);

  if (!connections.length) {
    return (
      <div className="mt-10 rounded-xl border border-dashed border-line bg-bg-surface p-12 text-center" data-testid="bh-no-connections">
        <RadioTower className="h-7 w-7 mx-auto text-cyan-300 mb-2" />
        <div className="text-base font-bold text-ink-primary">No broadcasting system connected yet</div>
        <div className="text-xs text-ink-muted mt-1">Connect Centova Cast, SHOUTcast, Icecast or a custom stream to light up this dashboard.</div>
        <button onClick={onNeedConnection} className="mt-4 inline-flex items-center gap-1.5 rounded-md bg-brand-amber px-4 py-2 text-xs font-semibold text-black" data-testid="bh-go-connect">
          <Plug className="h-3.5 w-3.5" /> Connect a system
        </button>
      </div>
    );
  }

  const snap = data?.snapshot;
  const np = snap?.now_playing || {};
  const ls = snap?.listeners || {};
  const stream = snap?.stream || {};

  return (
    <div className="mt-6" data-testid="bh-dashboard">
      <div className="flex flex-wrap items-center gap-2">
        {connections.length > 1 && (
          <select value={connId} onChange={(e) => { setConnId(e.target.value); load(e.target.value); }}
            className="rounded-md border border-line bg-bg px-3 py-2 text-sm text-ink-primary" data-testid="bh-conn-select">
            {connections.map((c) => <option key={c.conn_id} value={c.conn_id}>{c.label}</option>)}
          </select>
        )}
        {snap && (
          <span className={`inline-flex items-center gap-1.5 rounded-full px-3 py-1 text-[11px] font-semibold ${
            snap.ok && snap.online ? "bg-emerald-500/10 text-emerald-300 border border-emerald-500/40"
              : "bg-rose-500/10 text-rose-300 border border-rose-500/40"}`} data-testid="bh-stream-status">
            <span className={`h-2 w-2 rounded-full ${snap.ok && snap.online ? "bg-emerald-400 animate-pulse" : "bg-rose-400"}`} />
            {snap.ok && snap.online ? "STREAM ONLINE" : snap.ok ? "STREAM OFFLINE" : "CONNECTION ERROR"}
          </span>
        )}
        {snap?.autodj_on != null && (
          <span className="text-[11px] text-ink-muted">AutoDJ: <span className={snap.autodj_on ? "text-emerald-300" : "text-ink-secondary"}>{snap.autodj_on ? "running" : "stopped"}</span></span>
        )}
        <span className="ml-auto text-[11px] text-ink-muted">updated {timeAgo(snap?.fetched_at)}</span>
        <button onClick={() => load(connId || active.conn_id)} disabled={busy} data-testid="bh-refresh"
          className="inline-flex items-center gap-1 rounded-md border border-line bg-bg-elevated px-2 py-1 text-[11px] text-ink-secondary hover:text-ink-primary">
          {busy ? <Loader2 className="h-3 w-3 animate-spin" /> : <RefreshCw className="h-3 w-3" />} Refresh
        </button>
      </div>

      {snap && !snap.ok && (
        <div className="mt-3 rounded-lg border border-rose-500/40 bg-rose-500/5 px-3 py-2 text-xs text-rose-300" data-testid="bh-snap-error">
          {snap.error || "Could not read data from the provider"}
        </div>
      )}
      {snap?.push_waiting && (
        <div className="mt-3 rounded-lg border border-cyan-500/40 bg-cyan-500/5 px-3 py-2 text-xs text-cyan-300" data-testid="bh-push-waiting">
          Waiting for the first push from your playout software — copy the push URL from the Connections tab into it.
        </div>
      )}

      <div className="mt-4 grid lg:grid-cols-3 gap-4">
        <section className="lg:col-span-2 rounded-xl border border-line bg-bg-surface p-5">
          <div className="font-mono text-[10px] uppercase tracking-[0.22em] text-brand-amber mb-3">// NOW PLAYING</div>
          <div className="flex items-center gap-4">
            <div className="h-28 w-28 shrink-0 rounded-lg overflow-hidden border border-line bg-bg-elevated flex items-center justify-center">
              {data?.artwork_url
                ? <img src={data.artwork_url} alt="Album art" className="h-full w-full object-cover" data-testid="bh-artwork" />
                : <Disc3 className="h-10 w-10 text-ink-muted" />}
            </div>
            <div className="min-w-0">
              <div className="text-xl md:text-2xl font-black text-ink-primary truncate" data-testid="bh-np-title">{np.title || np.raw || "—"}</div>
              <div className="text-sm text-brand-amber font-semibold truncate" data-testid="bh-np-artist">{np.artist || "Unknown artist"}</div>
              <div className="mt-2 flex flex-wrap gap-3 text-[11px] text-ink-muted">
                {stream.station_name && <span>{stream.station_name}</span>}
                {stream.bitrate != null && <span>{stream.bitrate} kbps</span>}
                {stream.format && <span>{stream.format}</span>}
                {stream.server && <span>{stream.server}</span>}
              </div>
            </div>
          </div>

          <div className="mt-5">
            <div className="font-mono text-[10px] uppercase tracking-[0.22em] text-brand-amber mb-2">// RECENTLY PLAYED</div>
            {(data?.recent || []).length === 0 && <div className="text-xs text-ink-muted" data-testid="bh-no-recent">History builds automatically as tracks change.</div>}
            <div className="space-y-1">
              {(data?.recent || []).slice(0, 8).map((t, i) => (
                <div key={i} className="flex items-center gap-2 text-xs border-b border-line/50 py-1.5" data-testid={`bh-recent-${i}`}>
                  <Music2 className="h-3 w-3 text-ink-muted shrink-0" />
                  <span className="text-ink-primary truncate">{t.artist ? `${t.artist} — ${t.title}` : t.title}</span>
                  <span className="ml-auto text-ink-muted shrink-0">{t.played_at ? timeAgo(t.played_at) : ""}</span>
                </div>
              ))}
            </div>
          </div>
        </section>

        <aside className="space-y-3">
          <div className="grid grid-cols-2 gap-3">
            <Stat icon={Users} label="Listeners" value={ls.current ?? "—"} testid="bh-listeners" />
            <Stat icon={Activity} label="Peak" value={ls.peak ?? "—"} testid="bh-peak" />
            <Stat icon={Gauge} label="Bitrate" value={stream.bitrate != null ? `${stream.bitrate}k` : "—"} testid="bh-bitrate" />
            <Stat icon={ShieldCheck} label="Max slots" value={ls.max ?? "—"} testid="bh-max" />
          </div>
          <TrendCard samples={trends} />
          {active && <ControlsCard conn={active} onDone={() => load(active.conn_id)} />}
        </aside>
      </div>
    </div>
  );
}

function Stat({ icon: Icon, label, value, testid }) {
  return (
    <div className="rounded-xl border border-line bg-bg-surface p-3" data-testid={testid}>
      <div className="flex items-center gap-1.5 text-[10px] uppercase tracking-wider text-ink-muted"><Icon className="h-3 w-3" /> {label}</div>
      <div className="text-2xl font-black text-ink-primary mt-1">{value}</div>
    </div>
  );
}

function TrendCard({ samples }) {
  if (samples.length < 2) {
    return (
      <div className="rounded-xl border border-line bg-bg-surface p-3" data-testid="bh-trend-empty">
        <div className="text-[10px] uppercase tracking-wider text-ink-muted">Listener trend (24h)</div>
        <div className="text-xs text-ink-muted mt-2">Collecting data — the trend chart fills in as the dashboard polls.</div>
      </div>
    );
  }
  const vals = samples.map((s) => s.listeners);
  const max = Math.max(...vals, 1);
  const pts = vals.map((v, i) => `${(i / Math.max(vals.length - 1, 1)) * 100},${40 - (v / max) * 36}`).join(" ");
  return (
    <div className="rounded-xl border border-line bg-bg-surface p-3" data-testid="bh-trend">
      <div className="flex items-center justify-between">
        <div className="text-[10px] uppercase tracking-wider text-ink-muted">Listener trend (24h)</div>
        <div className="text-[10px] text-ink-muted">max {max}</div>
      </div>
      <svg viewBox="0 0 100 42" className="w-full h-16 mt-1" preserveAspectRatio="none">
        <polyline points={pts} fill="none" stroke="#f59e0b" strokeWidth="1.5" vectorEffect="non-scaling-stroke" />
      </svg>
    </div>
  );
}

const CONTROL_ACTIONS = [
  { id: "autodj_start", label: "Start AutoDJ", icon: Play, danger: false },
  { id: "autodj_stop", label: "Stop AutoDJ", icon: Square, danger: true },
  { id: "skip_track", label: "Skip track", icon: SkipForward, danger: false },
  { id: "reload", label: "Reload playlists", icon: RotateCcw, danger: false },
  { id: "restart", label: "Restart stream", icon: RefreshCw, danger: true },
];

const PROVIDER_CONTROL_IDS = {
  centova: ["autodj_start", "autodj_stop", "skip_track", "reload", "restart"],
  azuracast: ["autodj_start", "autodj_stop", "skip_track", "restart"],
};

function ControlsCard({ conn, onDone }) {
  const [busy, setBusy] = useState("");
  const [pls, setPls] = useState(null);
  const caps = conn.capabilities || {};
  const canControl = caps.autodj_control || caps.server_control;
  const actions = CONTROL_ACTIONS.filter((a) => (PROVIDER_CONTROL_IDS[conn.provider] || []).includes(a.id));
  if (!canControl && !caps.playlists) return null;

  const run = async (action, label, danger) => {
    const ok = await appConfirm(
      `${label} on "${conn.label}"?${danger ? " This can interrupt your live broadcast." : ""}`,
      { title: "Confirm broadcast control", confirmText: label, danger });
    if (!ok) return;
    setBusy(action);
    try {
      const { data } = await api.post(`/broadcast/connections/${conn.conn_id}/control`, { action, confirm: true });
      toast.success(`${label}: ${data.message || "done"}`);
      onDone?.();
    } catch (e) { toast.error(e?.response?.data?.detail || `${label} failed`); }
    finally { setBusy(""); }
  };

  const loadPls = async () => {
    try {
      const { data } = await api.get(`/broadcast/connections/${conn.conn_id}/playlists`);
      setPls(data.playlists || []);
    } catch (e) { toast.error(e?.response?.data?.detail || "Couldn't load playlists"); }
  };

  return (
    <div className="rounded-xl border border-line bg-bg-surface p-3" data-testid="bh-controls">
      <div className="text-[10px] uppercase tracking-wider text-ink-muted mb-2">AutoDJ &amp; server controls</div>
      {!conn.controls_enabled && canControl && (
        <div className="text-[11px] text-amber-300/90 mb-2" data-testid="bh-controls-disabled">
          Read-only mode. Enable controls for this connection in the Connections tab to use these.
        </div>
      )}
      {canControl && (
        <div className="flex flex-wrap gap-1.5">
          {actions.map((a) => (
            <button key={a.id} onClick={() => run(a.id, a.label, a.danger)}
              disabled={!conn.controls_enabled || !!busy}
              data-testid={`bh-control-${a.id}`}
              className={`inline-flex items-center gap-1 rounded-md border px-2 py-1 text-[11px] disabled:opacity-40 ${
                a.danger ? "border-rose-500/40 text-rose-300 hover:bg-rose-500/10" : "border-line text-ink-secondary hover:text-ink-primary hover:border-brand-amber/50"}`}>
              {busy === a.id ? <Loader2 className="h-3 w-3 animate-spin" /> : <a.icon className="h-3 w-3" />}
              {a.label}
            </button>
          ))}
        </div>
      )}
      {caps.playlists && (
        <div className="mt-3">
          <button onClick={loadPls} className="inline-flex items-center gap-1 text-[11px] text-cyan-300 hover:underline" data-testid="bh-load-playlists">
            <ListMusic className="h-3 w-3" /> View AutoDJ playlists
          </button>
          {pls && (
            <div className="mt-2 space-y-1">
              {pls.length === 0 && <div className="text-[11px] text-ink-muted">No playlists returned.</div>}
              {pls.map((p) => (
                <div key={p.id} className="flex items-center gap-2 text-[11px] border-b border-line/50 py-1" data-testid={`bh-playlist-${p.id}`}>
                  <span className="text-ink-primary truncate">{p.title}</span>
                  <span className="text-ink-muted">{p.type}</span>
                  <span className={`ml-auto ${p.status === "enabled" ? "text-emerald-300" : "text-ink-muted"}`}>{p.status}</span>
                </div>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

/* ─────────────────────────── Network Control Room ─────────────────────────── */

function NetworkTab() {
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState(false);
  const timer = useRef(null);

  const load = () => {
    setBusy(true);
    api.get("/broadcast/network/overview").then((r) => setData(r.data)).catch(() => {}).finally(() => setBusy(false));
  };
  useEffect(() => {
    load();
    timer.current = setInterval(load, 30000);
    return () => clearInterval(timer.current);
  }, []);

  if (!data) return <div className="mt-10 text-center text-ink-muted text-sm"><Loader2 className="h-5 w-5 mx-auto animate-spin text-brand-amber mb-2" />Scanning your network…</div>;

  return (
    <div className="mt-6" data-testid="bh-network">
      <div className="flex items-start gap-3">
        <div className="grid grid-cols-3 gap-3 max-w-xl flex-1">
          <Stat icon={Network} label="Connections" value={data.totals.connections} testid="bh-net-total" />
          <Stat icon={Activity} label="Online" value={data.totals.online} testid="bh-net-online" />
          <Stat icon={Users} label="Listeners" value={data.totals.listeners} testid="bh-net-listeners" />
        </div>
        <button onClick={load} disabled={busy} className="inline-flex items-center gap-1 rounded-md border border-line bg-bg-elevated px-2 py-1 text-[11px] text-ink-secondary hover:text-ink-primary shrink-0" data-testid="bh-net-refresh">
          {busy ? <Loader2 className="h-3 w-3 animate-spin" /> : <RefreshCw className="h-3 w-3" />} Refresh all
        </button>
      </div>

      {data.alerts.length > 0 && (
        <div className="mt-4 rounded-lg border border-rose-500/40 bg-rose-500/5 p-3" data-testid="bh-net-alerts">
          <div className="text-xs font-semibold text-rose-300 mb-1 flex items-center gap-1.5"><AlertTriangle className="h-3.5 w-3.5" /> Station alerts</div>
          {data.alerts.map((a) => (
            <div key={a.conn_id} className="text-[11px] text-rose-300/90">{a.station_name || a.label}: {a.problem}</div>
          ))}
        </div>
      )}

      <div className="mt-4 grid md:grid-cols-2 xl:grid-cols-3 gap-3">
        {data.stations.length === 0 && (
          <div className="text-xs text-ink-muted" data-testid="bh-net-empty">No broadcasting connections yet — add one in the Connections tab.</div>
        )}
        {data.stations.map((s) => {
          const snap = s.snapshot || {};
          const ok = snap.ok && snap.online;
          return (
            <div key={s.connection.conn_id} className="rounded-xl border border-line bg-bg-surface p-4" data-testid={`bh-net-card-${s.connection.conn_id}`}>
              <div className="flex items-center gap-2">
                <span className={`h-2.5 w-2.5 rounded-full shrink-0 ${ok ? "bg-emerald-400 animate-pulse" : "bg-rose-400"}`} />
                <div className="font-bold text-ink-primary text-sm truncate">{s.station_name || s.connection.label}</div>
                <span className="ml-auto text-[10px] font-mono uppercase text-ink-muted">{s.connection.provider}</span>
              </div>
              <div className="mt-2 text-xs text-ink-secondary truncate">
                {ok ? ((snap.now_playing?.raw) || "Nothing playing") : (snap.error || "Offline")}
              </div>
              <div className="mt-2 flex gap-3 text-[11px] text-ink-muted">
                <span><Users className="h-3 w-3 inline mr-1" />{(snap.listeners?.current) ?? "—"}</span>
                {snap.stream?.bitrate != null && <span>{snap.stream.bitrate}k</span>}
                <span className="ml-auto">{timeAgo(snap.fetched_at)}</span>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

/* ─────────────────────────── Connections + Wizard ─────────────────────────── */

function ConnectionsTab({ connections, reload }) {
  const [adding, setAdding] = useState(false);
  return (
    <div className="mt-6" data-testid="bh-connections">
      {!adding && (
        <button onClick={() => setAdding(true)} className="inline-flex items-center gap-1.5 rounded-md bg-brand-amber px-4 py-2 text-xs font-semibold text-black amber-glow" data-testid="bh-add-connection">
          <Plug className="h-3.5 w-3.5" /> Connect a broadcasting system
        </button>
      )}
      {adding && <Wizard onClose={() => setAdding(false)} onSaved={() => { setAdding(false); reload(); }} />}

      <div className="mt-5 space-y-3">
        {connections.map((c) => <ConnectionRow key={c.conn_id} conn={c} reload={reload} />)}
        {!connections.length && !adding && (
          <div className="text-xs text-ink-muted mt-4">No connections yet. Your credentials are encrypted at rest and never shown again after saving.</div>
        )}
      </div>
    </div>
  );
}

function ConnectionRow({ conn, reload }) {
  const [busy, setBusy] = useState(false);
  const caps = Object.entries(conn.capabilities || {}).filter(([, v]) => v).map(([k]) => CAP_LABELS[k] || k);
  const pushUrl = conn.push_token
    ? `${process.env.REACT_APP_BACKEND_URL}/api/broadcast/push/${conn.push_token}?artist=$artist$&title=$title$`
    : "";

  const copyPush = async () => {
    try {
      await navigator.clipboard.writeText(pushUrl);
      toast.success("Push URL copied");
    } catch { toast.error("Couldn't copy — select and copy the URL manually"); }
  };

  const regenPush = async () => {
    const ok = await appConfirm(
      "Generate a new push URL? The old one stops working immediately — update it in your playout software.",
      { title: "Regenerate push token", confirmText: "Regenerate" });
    if (!ok) return;
    try {
      await api.post(`/broadcast/connections/${conn.conn_id}/regenerate-push-token`);
      toast.success("New push URL generated");
      reload();
    } catch (e) { toast.error(e?.response?.data?.detail || "Couldn't regenerate"); }
  };

  const toggleControls = async () => {
    if (!conn.controls_enabled) {
      const ok = await appConfirm(
        "Enable AutoDJ / server controls for this connection? BroadcastFlow will still ask you to confirm every individual action.",
        { title: "Enable broadcast controls", confirmText: "Enable controls" });
      if (!ok) return;
    }
    setBusy(true);
    try {
      await api.patch(`/broadcast/connections/${conn.conn_id}`, { controls_enabled: !conn.controls_enabled });
      toast.success(!conn.controls_enabled ? "Controls enabled" : "Back to read-only");
      reload();
    } catch (e) { toast.error(e?.response?.data?.detail || "Couldn't update"); }
    finally { setBusy(false); }
  };

  const remove = async () => {
    const ok = await appConfirm(`Remove "${conn.label}"? This only deletes the BroadcastFlow connection — your radio server is untouched.`,
      { title: "Remove connection", confirmText: "Remove", danger: true });
    if (!ok) return;
    try {
      await api.delete(`/broadcast/connections/${conn.conn_id}`);
      toast.success("Connection removed");
      reload();
    } catch (e) { toast.error(e?.response?.data?.detail || "Couldn't remove"); }
  };

  const useForNowPlaying = async () => {
    try {
      const { data } = await api.post(`/broadcast/connections/${conn.conn_id}/use-for-nowplaying`);
      toast.success(`Now Playing source set: ${data.stream_url}`, { duration: 7000 });
    } catch (e) { toast.error(e?.response?.data?.detail || "Couldn't set source"); }
  };

  return (
    <div className="rounded-xl border border-line bg-bg-surface p-4" data-testid={`bh-conn-${conn.conn_id}`}>
      <div className="flex flex-wrap items-center gap-2">
        <span className={`h-2.5 w-2.5 rounded-full ${conn.last_check_ok ? "bg-emerald-400" : "bg-rose-400"}`} />
        <div className="font-bold text-ink-primary">{conn.label}</div>
        <span className="text-[10px] font-mono uppercase tracking-wider rounded-full border border-line px-2 py-0.5 text-ink-muted">{conn.provider}</span>
        <span className="text-[11px] text-ink-muted truncate">{conn.base_url}</span>
        <span className="ml-auto text-[11px] text-ink-muted">checked {timeAgo(conn.last_check_at)}</span>
      </div>
      {conn.last_error && <div className="mt-1 text-[11px] text-rose-300">{conn.last_error}</div>}
      {conn.push_token && (
        <div className="mt-3 rounded-lg border border-cyan-500/30 bg-cyan-500/5 p-3" data-testid={`bh-push-setup-${conn.conn_id}`}>
          <div className="text-[10px] font-mono uppercase tracking-wider text-cyan-300 mb-1.5">Now-playing push setup</div>
          <div className="flex flex-wrap items-center gap-2">
            <code className="text-[10px] text-ink-secondary break-all flex-1 min-w-[200px]" data-testid={`bh-push-url-${conn.conn_id}`}>{pushUrl}</code>
            <button onClick={copyPush} className="rounded-md border border-line px-2 py-1 text-[11px] text-ink-secondary hover:text-ink-primary shrink-0" data-testid={`bh-push-copy-${conn.conn_id}`}>Copy</button>
            <button onClick={regenPush} className="rounded-md border border-line px-2 py-1 text-[11px] text-ink-secondary hover:text-ink-primary shrink-0" data-testid={`bh-push-regen-${conn.conn_id}`}>New token</button>
          </div>
          <div className="mt-1.5 text-[10px] text-ink-muted leading-relaxed">
            {conn.provider === "radiodj"
              ? "RadioDJ: Options → Plugins → Now Playing Info → Web Export — paste this URL (method GET, keep the $artist$/$title$ wildcards) and enable the plugin."
              : "Myriad: add an HTTP now-playing / metadata output (OCP or Now Playing export) using this URL — replace $artist$/$title$ with Myriad's own wildcards."}
          </div>
          <div className="mt-1 text-[10px] font-mono" data-testid={`bh-push-last-${conn.conn_id}`}>
            {conn.push_np?.at
              ? <span className="text-emerald-300">Last push {timeAgo(conn.push_np.at)} — {conn.push_np.raw}</span>
              : <span className="text-amber-300">No push received yet — waiting for the first track</span>}
          </div>
        </div>
      )}
      <div className="mt-2 flex flex-wrap gap-1">
        {caps.map((c) => (
          <span key={c} className="rounded-full bg-cyan-500/10 border border-cyan-500/30 px-2 py-0.5 text-[10px] text-cyan-300">{c}</span>
        ))}
      </div>
      <div className="mt-3 flex flex-wrap items-center gap-2">
        {(conn.capabilities?.autodj_control || conn.capabilities?.server_control) && (
          <button onClick={toggleControls} disabled={busy} data-testid={`bh-conn-controls-${conn.conn_id}`}
            className={`inline-flex items-center gap-1 rounded-md border px-2 py-1 text-[11px] ${
              conn.controls_enabled ? "border-emerald-500/40 text-emerald-300" : "border-line text-ink-secondary hover:text-ink-primary"}`}>
            <ShieldCheck className="h-3 w-3" /> {conn.controls_enabled ? "Controls: ON" : "Read-only (enable controls)"}
          </button>
        )}
        {conn.capabilities?.now_playing && (
          <button onClick={useForNowPlaying} className="inline-flex items-center gap-1 rounded-md border border-line px-2 py-1 text-[11px] text-ink-secondary hover:text-ink-primary" data-testid={`bh-conn-usenp-${conn.conn_id}`}>
            <Rss3 /> Use for Now Playing auto-posts
          </button>
        )}
        <button onClick={remove} className="ml-auto inline-flex items-center gap-1 rounded-md border border-rose-500/40 px-2 py-1 text-[11px] text-rose-300 hover:bg-rose-500/10" data-testid={`bh-conn-delete-${conn.conn_id}`}>
          <Trash2 className="h-3 w-3" /> Remove
        </button>
      </div>
    </div>
  );
}

const Rss3 = () => <Activity className="h-3 w-3" />;

function Wizard({ onClose, onSaved }) {
  const [providers, setProviders] = useState([]);
  const [step, setStep] = useState(1);
  const [provider, setProvider] = useState(null);
  const [form, setForm] = useState({ base_url: "", username: "", password: "", api_key: "", mount: "", sid: "1", label: "" });
  const [testing, setTesting] = useState(false);
  const [result, setResult] = useState(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    api.get("/broadcast/providers").then((r) => setProviders(r.data.providers || [])).catch(() => {});
  }, []);

  const runTest = async () => {
    setTesting(true);
    setResult(null);
    try {
      const { data } = await api.post("/broadcast/connections/test", { provider: provider.id, ...form });
      setResult(data);
      if (data.ok) setStep(3);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Test failed");
    } finally { setTesting(false); }
  };

  const save = async () => {
    setSaving(true);
    try {
      await api.post("/broadcast/connections", { provider: provider.id, ...form });
      toast.success("Broadcasting system connected");
      onSaved();
    } catch (e) { toast.error(e?.response?.data?.detail || "Couldn't save connection"); }
    finally { setSaving(false); }
  };

  const fields = provider?.fields || [];

  return (
    <div className="rounded-xl border border-brand-amber/40 bg-bg-surface p-5" data-testid="bh-wizard">
      <div className="flex items-center justify-between">
        <div className="font-mono text-[10px] uppercase tracking-[0.22em] text-brand-amber">// CONNECTION WIZARD — STEP {step} OF 3</div>
        <button onClick={onClose} className="text-ink-muted hover:text-ink-primary" data-testid="bh-wizard-close"><XIcon className="h-4 w-4" /></button>
      </div>

      {step === 1 && (
        <div className="mt-4">
          <div className="text-sm font-bold text-ink-primary mb-2">Choose your broadcasting provider</div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-2">
            {providers.map((p) => (
              <button key={p.id} disabled={!p.implemented}
                onClick={() => {
                  if (p.external_page) { window.location.href = p.external_page; return; }
                  setProvider(p); setStep(2); setResult(null);
                  setForm((f) => ({ ...f, sid: ["radioco", "live365", "radioking"].includes(p.id) ? "" : f.sid || "1" }));
                }}
                data-testid={`bh-provider-${p.id}`}
                className={`text-left rounded-lg border p-3 transition ${
                  p.implemented ? "border-line bg-bg hover:border-brand-amber/60" : "border-line/50 bg-bg opacity-50 cursor-not-allowed"}`}>
                <div className="flex items-center gap-2">
                  <RadioTower className="h-4 w-4 text-brand-amber shrink-0" />
                  <span className="text-xs font-bold text-ink-primary">{p.name}</span>
                </div>
                <div className="mt-1 text-[10px] text-ink-muted">
                  {p.implemented ? (p.external_page ? "Configured on its own page" : "Available now") : `Coming in Phase ${p.phase}`}
                </div>
              </button>
            ))}
          </div>
        </div>
      )}

      {step === 2 && provider && (
        <div className="mt-4 max-w-xl">
          <div className="flex items-center gap-2 text-sm font-bold text-ink-primary">
            {provider.name} <ChevronRight className="h-3 w-3 text-ink-muted" /> enter connection details
          </div>
          <p className="mt-1 text-[11px] text-ink-muted leading-relaxed">{provider.guide}</p>
          <div className="mt-3 space-y-3">
            <div>
              <Label className="text-[10px] uppercase tracking-wider text-ink-muted">Connection name</Label>
              <Input value={form.label} onChange={(e) => setForm({ ...form, label: e.target.value })}
                placeholder={provider.name} className="mt-1 bg-bg border-line" data-testid="bh-f-label" />
            </div>
            {fields.includes("base_url") && (
              <div>
                <Label className="text-[10px] uppercase tracking-wider text-ink-muted">
                  {provider.id === "centova" ? "Panel URL (with port 2199)"
                    : provider.base_url_optional ? "Stream server URL (optional)" : "Server / stream URL"}
                </Label>
                <Input value={form.base_url} onChange={(e) => setForm({ ...form, base_url: e.target.value })}
                  placeholder={provider.example_url} className="mt-1 bg-bg border-line" data-testid="bh-f-url" />
              </div>
            )}
            {fields.includes("username") && (
              <div>
                <Label className="text-[10px] uppercase tracking-wider text-ink-muted">Username</Label>
                <Input value={form.username} onChange={(e) => setForm({ ...form, username: e.target.value })}
                  className="mt-1 bg-bg border-line" data-testid="bh-f-username" autoComplete="off" />
              </div>
            )}
            {fields.includes("password") && (
              <div>
                <Label className="text-[10px] uppercase tracking-wider text-ink-muted">Password</Label>
                <Input type="password" value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })}
                  className="mt-1 bg-bg border-line" data-testid="bh-f-password" autoComplete="new-password" />
                <div className="text-[10px] text-ink-muted mt-0.5">Encrypted at rest. HTTPS required. Never shown again after saving.</div>
              </div>
            )}
            {fields.includes("api_key") && (
              <div>
                <Label className="text-[10px] uppercase tracking-wider text-ink-muted">
                  API key{provider.id === "azuracast" ? " (optional — unlocks playlists & controls)" : ""}
                </Label>
                <Input type="password" value={form.api_key} onChange={(e) => setForm({ ...form, api_key: e.target.value })}
                  className="mt-1 bg-bg border-line" data-testid="bh-f-apikey" autoComplete="new-password" />
                <div className="text-[10px] text-ink-muted mt-0.5">Encrypted at rest. HTTPS required. Never shown again after saving.</div>
              </div>
            )}
            {fields.includes("mount") && (
              <div>
                <Label className="text-[10px] uppercase tracking-wider text-ink-muted">
                  {provider.id === "mediacp" ? "Service name / unique ID (optional)" : "Mount point (optional)"}
                </Label>
                <Input value={form.mount} onChange={(e) => setForm({ ...form, mount: e.target.value })}
                  placeholder={provider.id === "mediacp" ? "my-radio" : "/stream"} className="mt-1 bg-bg border-line" data-testid="bh-f-mount" />
              </div>
            )}
            {fields.includes("sid") && (
              <div>
                <Label className="text-[10px] uppercase tracking-wider text-ink-muted">
                  {{ azuracast: "Station ID / shortcode", everestcast: "Station ID",
                     radioco: "Station ID (starts with 's')", live365: "Station ID (starts with 'a')",
                     radioking: "Radio slug (from your listen URL)" }[provider.id] || "Stream ID (SID)"}
                </Label>
                <Input value={form.sid} onChange={(e) => setForm({ ...form, sid: e.target.value })}
                  placeholder={{ radioco: "s96f91db2a", live365: "a12345", radioking: "my-radio" }[provider.id] || ""}
                  className="mt-1 bg-bg border-line w-56" data-testid="bh-f-sid" />
              </div>
            )}
          </div>

          <div className="mt-4 flex items-center gap-2">
            <button onClick={() => setStep(1)} className="rounded-md border border-line px-3 py-2 text-xs text-ink-secondary" data-testid="bh-wizard-back">Back</button>
            <button onClick={runTest}
              disabled={testing
                || (fields.includes("base_url") && !provider.base_url_optional && !form.base_url)
                || (fields.includes("sid") && !fields.includes("base_url") && !form.sid.trim())}
              className="inline-flex items-center gap-2 rounded-md bg-brand-amber px-4 py-2 text-xs font-semibold text-black amber-glow disabled:opacity-50" data-testid="bh-wizard-test">
              {testing ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Plug className="h-3.5 w-3.5" />} Test connection
            </button>
          </div>

          {result && (
            <div className="mt-4 space-y-1.5" data-testid="bh-test-steps">
              {result.steps.map((s, i) => (
                <div key={i} className="flex items-start gap-2 text-xs">
                  {s.status === "pass"
                    ? <CheckCircle2 className="h-3.5 w-3.5 text-emerald-400 mt-0.5 shrink-0" />
                    : <AlertTriangle className="h-3.5 w-3.5 text-rose-400 mt-0.5 shrink-0" />}
                  <span className="text-ink-primary font-semibold shrink-0">{s.name}</span>
                  <span className="text-ink-muted whitespace-pre-wrap">{s.note}</span>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {step === 3 && result && (
        <div className="mt-4 max-w-xl" data-testid="bh-wizard-summary">
          <div className="flex items-center gap-2 text-sm font-bold text-emerald-300">
            <CheckCircle2 className="h-4 w-4" /> All checks passed — ready to save
          </div>
          {result.snapshot?.now_playing?.raw && (
            <div className="mt-2 text-xs text-ink-secondary">Live right now: <span className="text-ink-primary font-semibold">{result.snapshot.now_playing.raw}</span></div>
          )}
          <div className="mt-3">
            <div className="text-[10px] uppercase tracking-wider text-ink-muted mb-1">Detected capabilities</div>
            <div className="flex flex-wrap gap-1">
              {Object.entries(result.capabilities || {}).filter(([, v]) => v).map(([k]) => (
                <span key={k} className="rounded-full bg-cyan-500/10 border border-cyan-500/30 px-2 py-0.5 text-[10px] text-cyan-300">{CAP_LABELS[k] || k}</span>
              ))}
            </div>
          </div>
          <div className="mt-2 text-[11px] text-ink-muted">
            Saved read-only. AutoDJ / server controls stay OFF until you explicitly enable them, and every action asks for confirmation.
          </div>
          <div className="mt-4 flex items-center gap-2">
            <button onClick={() => setStep(2)} className="rounded-md border border-line px-3 py-2 text-xs text-ink-secondary">Back</button>
            <button onClick={save} disabled={saving}
              className="inline-flex items-center gap-2 rounded-md bg-brand-amber px-5 py-2 text-xs font-semibold text-black amber-glow disabled:opacity-50" data-testid="bh-wizard-save">
              {saving ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <CheckCircle2 className="h-3.5 w-3.5" />} Save connection
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
